<template>
  <div class="filter-bar-C">
    <r-flex align="flex-end" class="r-mb-8" v-if="showFirstRow">
      <!-- İlk satır filtreleri buraya gelecek -->
      <slot name="first-row"></slot>
    </r-flex>

    <r-flex align="flex-end" class="r-mb-8">
      <!-- İkinci satır filtreleri buraya gelecek -->
      <slot name="second-row"></slot>

      <!-- Varsayılan filtre butonu (opsiyonel) -->
      <r-btn
        v-if="showFilterButton"
        bgcolor="#1e88e5"
        @click="handleFilter"
        :disabled="isLoading"
      >
        {{ filterButtonText }}
      </r-btn>

      <!-- Ekstra butonlar için slot -->
      <slot name="actions"></slot>
    </r-flex>

    <!-- Üçüncü satır: Dinamik Property Filtreleri (Multiple Select) -->
    <r-flex align="flex-start" class="r-mb-8" v-if="showDynamicProperties">
      <div
        v-if="isDynamicLoading"
        style="font-size: 12px; color: #666; margin-right: 16px;"
      >
        Dinamik özellikler yükleniyor...
      </div>
      <div
        v-else-if="dynamicLoadError"
        style="font-size: 12px; color: #b71c1c; margin-right: 16px;"
      >
        Dinamik özellikler yüklenirken bir hata oluştu.
      </div>
      <template v-if="propertyDefinitions.length > 0">
        <div
          v-for="propDef in propertyDefinitions"
          :key="propDef.id"
          class="dynamic-filter-property-C"
          style="margin-right: 16px;"
        >
          <label
            style="display: block; margin-bottom: 6px; font-size: 13px; font-weight: 500; color: #333;"
          >
            {{ propDef.name }}
          </label>
          <div
            v-if="propDef.values && propDef.values.length > 0"
            style="display: flex; flex-wrap: wrap; gap: 8px;"
          >
            <r-checkbox
              v-for="val in propDef.values"
              :key="`filter-${propDef.id}-${val.id}`"
              v-model="selectedPropertyValues[propDef.id]"
              :val="val.id"
              :label="val.displayText || val.value"
              :disabled="isLoading"
              @change="() => emitPropertyChange(propDef.id)"
            />
          </div>
          <div
            v-else
            style="padding: 6px; background: #fff3cd; border: 1px solid #ffc107; border-radius: 3px; color: #856404; font-size: 11px;"
          >
            ⚠ Seçenek bulunamadı
          </div>
        </div>
      </template>
    </r-flex>
  </div>
</template>

<script>
import { ref, reactive, onMounted, watch } from "vue";
import { useStore } from "vuex";

export default {
  name: "FilterBar",
  props: {
    showFirstRow: {
      type: Boolean,
      default: true
    },
    showFilterButton: {
      type: Boolean,
      default: true
    },
    filterButtonText: {
      type: String,
      default: "Filtre"
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    showDynamicProperties: {
      type: Boolean,
      default: true
    },
    initialPropertyValues: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ["filter", "property-change"],
  setup(props, { emit }) {
    const store = useStore();
    const propertyDefinitions = ref([]);
    const selectedPropertyValues = reactive({});
    const isDynamicLoading = ref(false);
    const dynamicLoadError = ref(null);

    const handleFilter = () => {
      emit("filter", {
        propertyValues: selectedPropertyValues
      });
    };

    const emitPropertyChange = propertyDefinitionId => {
      const values = selectedPropertyValues[propertyDefinitionId] || [];
      emit("property-change", {
        propertyDefinitionId,
        selectedValues: Array.isArray(values) ? [...values] : []
      });
    };

    const resetDynamicProperties = () => {
      propertyDefinitions.value.forEach(propDef => {
        selectedPropertyValues[propDef.id] = [];
        emitPropertyChange(propDef.id);
      });
    };

    const getPropertyDefinitions = async () => {
      isDynamicLoading.value = true;
      dynamicLoadError.value = null;
      try {
        // Önce aktif property definitions'ları çek
        const res = await store.dispatch("getDatas", {
          api: "PropertyDefinitions/active"
        });
        const arr = res && (res.data ?? res.Data);
        const definitions = arr != null ? arr : [];

        // Sadece multiple cardinality olanları filtrele (cardinality === 2)
        const multipleDefinitions = definitions.filter(
          def => def.cardinality === 2
        );

        // Her property definition için detail endpoint'ini çağırarak values'ları da çek
        const definitionsWithValues = await Promise.all(
          multipleDefinitions.map(async def => {
            try {
              const detailRes = await store.dispatch("getDatas", {
                api: `PropertyDefinitions/${def.id}/detail`
              });
              if (detailRes && detailRes.result && detailRes.data) {
                // Detail'den gelen values'ları kullan
                return {
                  ...def,
                  values:
                    detailRes.data.values ||
                    detailRes.data.propertyDefinitionValues ||
                    []
                };
              }
              // Fallback: aktif endpoint'ten gelen values'ları kullan
              return {
                ...def,
                values: def.values || def.propertyDefinitionValues || []
              };
            } catch (err) {
              console.warn(
                `[WARN] Failed to fetch detail for property ${def.id}:`,
                err
              );
              return {
                ...def,
                values: def.values || def.propertyDefinitionValues || []
              };
            }
          })
        );

        propertyDefinitions.value = definitionsWithValues;

        // Her multiple cardinality property için başlangıçta seçili değerleri ayarla (initialPropertyValues varsa)
        definitionsWithValues.forEach(propDef => {
          const initialValues = props.initialPropertyValues[propDef.id];
          if (Array.isArray(initialValues)) {
            selectedPropertyValues[propDef.id] = [...initialValues];
          } else if (!selectedPropertyValues[propDef.id]) {
            selectedPropertyValues[propDef.id] = [];
          }
        });

        return definitionsWithValues;
      } catch (error) {
        console.error("[ERROR] Failed to fetch property definitions:", error);
        propertyDefinitions.value = [];
        dynamicLoadError.value = error;
        return [];
      } finally {
        isDynamicLoading.value = false;
      }
    };

    onMounted(async () => {
      if (props.showDynamicProperties) {
        await getPropertyDefinitions();
      }
    });

    watch(
      () => props.showDynamicProperties,
      async val => {
        if (val && propertyDefinitions.value.length === 0) {
          await getPropertyDefinitions();
        }
      }
    );

    return {
      handleFilter,
      propertyDefinitions,
      selectedPropertyValues,
      isDynamicLoading,
      dynamicLoadError,
      emitPropertyChange,
      resetDynamicProperties
    };
  }
};
</script>

<style scoped>
.filter-bar-C {
  width: 100%;
  margin-bottom: 16px;
}

.dynamic-filter-property-C {
  /* Tüm satırı kullanarak soldan sağa yayıl */
  min-width: 100%;
  max-width: 100%;
}

/* Dinamik soru etiketlerini (checkbox'lar) yatay dizmek için */
.dynamic-filter-property-C :deep(.r-checkbox-C) {
  width: auto;
}
.dynamic-filter-property-C :deep(.r-checkbox-C label) {
  width: auto;
}
</style>
